import {
  type ChainFullId,
  type ChainId,
  ChainIds,
  type MultichainTokenBalance,
} from "@umami/chains";
import { type UnifiedBalanceWithPrices } from "@umami/state";

import {
  type EffectiveOutput,
  type QuoteProvider,
  type QuoteWithProvider,
  type TezosEtherlinkQuote,
} from "./types";
import {
  ETHERLINK_TO_TEZOS_FEE,
  SwapValidator,
  TEZOS_TO_ETHERLINK_FEE,
} from "../validation/swapValidation";

export class TezosEtherlinkProvider implements QuoteProvider {
  canHandleSwap(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): boolean {
    return SwapValidator.isTezosEtherlinkSwap(sellToken, buyToken, getNetworkChain);
  }

  async fetchQuote(params: {
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    sellAmount: string;
    userAddress: string;
    network: string;
    slippage: number;
    isGaslessSwap?: boolean;
    getNetworkChain: (chainId: ChainFullId) => ChainId;
  }): Promise<QuoteWithProvider | null> {
    const { sellToken, buyToken, sellAmount, getNetworkChain } = params;

    try {
      // For Tezos-Etherlink swaps, the buy amount is the same as sell amount
      // (minus fees which are handled separately)
      const direction = this.getDirection(sellToken, buyToken, getNetworkChain);
      const fee = this.getFee(direction);

      const quote: TezosEtherlinkQuote = {
        provider: "TezosEtherlink",
        quote: {
          direction,
          fee,
          estimatedTime: "~5 minutes",
        },
        buyAmount: sellAmount,
        sellAmount,
      };

      return Promise.resolve(quote);
    } catch (error) {
      console.error("Error creating Tezos-Etherlink quote:", error);
      throw new Error(
        `Failed to create Tezos-Etherlink quote: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  }

  getValueAndUnit(
    quote: QuoteWithProvider,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getTokenUsdPrice: (symbol: string, amount: number) => number
  ): EffectiveOutput {
    const buyAmountNum = parseFloat(quote.buyAmount);
    const buyAmountUSD = getTokenUsdPrice(buyToken.symbol, buyAmountNum);
    const tezosQuote = quote.quote as TezosEtherlinkQuote["quote"];
    const feeUSD = getTokenUsdPrice("XTZ", tezosQuote.fee);

    if (buyAmountUSD > 0 && feeUSD > 0) {
      const netUSD = buyAmountUSD - feeUSD;

      return { value: netUSD, unit: "USD", hasUSD: true };
    }

    return { value: buyAmountNum, unit: buyToken.symbol, hasUSD: false };
  }

  private getDirection(
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance,
    getNetworkChain: (chainId: ChainFullId) => ChainId
  ): "tezos-to-etherlink" | "etherlink-to-tezos" {
    if (
      getNetworkChain(sellToken.chainId) === ChainIds.TEZOS &&
      getNetworkChain(buyToken.chainId) === ChainIds.ETHERLINK
    ) {
      return "tezos-to-etherlink";
    } else {
      return "etherlink-to-tezos";
    }
  }

  private getFee(direction: "tezos-to-etherlink" | "etherlink-to-tezos"): number {
    switch (direction) {
      case "tezos-to-etherlink":
        return TEZOS_TO_ETHERLINK_FEE;
      case "etherlink-to-tezos":
        return ETHERLINK_TO_TEZOS_FEE;
      default:
        return 0;
    }
  }
}
